import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faInfoCircle, faLock } from '@fortawesome/free-solid-svg-icons';

const OpenRegistersCloseModal = ({ show, registers, onCancel, onConfirm, loading }) => {
	const [registerInputs, setRegisterInputs] = useState([]);
	const [reason, setReason] = useState('');
	const [reasonError, setReasonError] = useState('');
	const [confirmed, setConfirmed] = useState(false);
	const [inputErrors, setInputErrors] = useState({});
	const [touched, setTouched] = useState({});

	useEffect(() => {
		if (show && registers.length > 0) {
			setRegisterInputs(
				registers.map((r) => ({
					id: r.id,
					closing_amount: r.current_amount || 0,
					expected_closing_amount: r.current_amount || 0,
					credit_card_slips: 0,
					cheques: 0
				}))
			);
			setReason('');
			setReasonError('');
			setConfirmed(false);
			setInputErrors({});
			setTouched({});
		}
	}, [show, registers]);

	const handleInputChange = (registerId, field, value) => {
		const numericValue = value === '' ? '' : Number(value);

		setRegisterInputs((prev) =>
			prev.map((item) =>
				item.id === registerId ? { ...item, [field]: numericValue } : item
			)
		);

		setTouched(prev => ({
			...prev,
			[registerId]: {
				...(prev[registerId] || {}),
				[field]: true
			}
		}));

		if (inputErrors[registerId] && inputErrors[registerId][field]) {
			const updatedErrors = { ...inputErrors };
			delete updatedErrors[registerId][field];
			if (Object.keys(updatedErrors[registerId] || {}).length === 0) {
				delete updatedErrors[registerId];
			}
			setInputErrors(updatedErrors);
		}
	};

	const handleFieldBlur = (registerId, field) => {
		setTouched(prev => ({
			...prev,
			[registerId]: {
				...(prev[registerId] || {}),
				[field]: true
			}
		}));
	};

	const validateInputs = () => {
		const errors = {};
		let isValid = true;

		registerInputs.forEach((input) => {
			const register = registers.find(r => r.id === input.id);
			const registerName = register?.BillingCounter?.counter_name || `Register ${input.id}`;
			const fieldErrors = {};

			if (input.closing_amount === '' || input.closing_amount === undefined || input.closing_amount === null || input.closing_amount < 0) {
				fieldErrors.closing_amount = 'Required';
				isValid = false;
			}

			if (input.credit_card_slips === '' || input.credit_card_slips === undefined || input.credit_card_slips === null || input.credit_card_slips < 0) {
				fieldErrors.credit_card_slips = 'Required';
				isValid = false;
			}

			if (input.cheques === '' || input.cheques === undefined || input.cheques === null || input.cheques < 0) {
				fieldErrors.cheques = 'Required';
				isValid = false;
			}

			if (Object.keys(fieldErrors).length > 0) {
				errors[input.id] = {
					registerName,
					...fieldErrors
				};
			}
		});

		setInputErrors(errors);
		return isValid;
	};

	const handleConfirm = () => {
		const allTouched = {};
		registerInputs.forEach(input => {
			allTouched[input.id] = {
				closing_amount: true,
				credit_card_slips: true,
				cheques: true
			};
		});
		setTouched(allTouched);

		if (!reason.trim() || reason.trim().length < 10) {
			setReasonError('Minimum 10 characters required.');
			return;
		}
		if (!confirmed) {
			return;
		}

		if (!validateInputs()) {
			return;
		}

		setReasonError('');
		onConfirm({ reason, registerInputs });
	};

	const shouldShowError = (registerId, field) => {
		return touched[registerId] && touched[registerId][field] && inputErrors[registerId] && inputErrors[registerId][field];
	};

	return (
		<Modal show={show} onHide={onCancel} backdrop="static" keyboard={false} size="lg" centered style={{ '--bs-modal-width': '1100px' }}>
			<Modal.Header closeButton className="py-2">
				<Modal.Title className="fs-6">
					<FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
					Open Registers Detected
				</Modal.Title>
			</Modal.Header>

			<Modal.Body className="py-2">
				<Alert variant="danger" className="py-1 mb-2" style={{ fontSize: '0.85rem' }}>
					<p className="mb-0">One or more registers are currently open for this business day.</p>
					<p className="mb-0">Proceeding will automatically close the listed register(s) and complete the business day closure.</p>
				</Alert>

				<h6 className="mt-2 mb-1 fw-bold" style={{ fontSize: '0.9rem' }}>Open Registers ({registers.length})</h6>

				<div
					className="table-responsive mb-2"
					style={{
						maxHeight: '300px',
						overflowY: 'auto',
						border: '1px solid #dee2e6',
						borderRadius: '4px',
						position: 'relative'
					}}
				>
					<Table hover className="align-middle mb-0" style={{ fontSize: '12px', width: '100%', tableLayout: 'fixed' }}>
						<thead style={{
							fontSize: '7px',  // REDUCED from 11px to 9px
							fontWeight: 600,
							position: 'sticky',
							top: 0,
							zIndex: 2,
							backgroundColor: '#dee2e6'
						}}>
							<tr>
								<th style={{
									width: '30px',
									minWidth: '30px',
									maxWidth: '30px',
									padding: '4px 2px',
									textAlign: 'center',
									backgroundColor: '#e9ecef',
									borderBottom: '2px solid #dee2e6'
								}}>#</th>
								<th style={{
									width: '10%',
									padding: '4px 6px',
									backgroundColor: '#e9ecef',
									borderBottom: '2px solid #dee2e6',
									wordWrap: 'break-word',
									whiteSpace: 'normal',
									lineHeight: '1.2'
								}}>Register</th>
								<th style={{
									width: '10%',
									padding: '4px 6px',
									backgroundColor: '#e9ecef',
									borderBottom: '2px solid #dee2e6',
									wordWrap: 'break-word',
									whiteSpace: 'normal',
									lineHeight: '1.2'
								}}>Opened By</th>
								<th style={{
									width: '12%',
									padding: '4px 6px',
									backgroundColor: '#e9ecef',
									borderBottom: '2px solid #dee2e6',
									wordWrap: 'break-word',
									whiteSpace: 'normal',
									lineHeight: '1.2'
								}}>Opened At</th>
								<th style={{
									width: '12%',
									padding: '4px 6px',
									textAlign: 'right',
									backgroundColor: '#e9ecef',
									borderBottom: '2px solid #dee2e6',
									wordWrap: 'break-word',
									whiteSpace: 'normal',
									lineHeight: '1.2'
								}}>Opening<br/>Amount</th>
								<th style={{
									width: '12%',
									padding: '4px 6px',
									textAlign: 'right',
									backgroundColor: '#e9ecef',
									borderBottom: '2px solid #dee2e6',
									wordWrap: 'break-word',
									whiteSpace: 'normal',
									lineHeight: '1.2'
								}}>Total Order Amt.</th>
								<th style={{
									width: '14%',
									padding: '4px 6px',
									backgroundColor: '#e9ecef',
									borderBottom: '2px solid #dee2e6',
									wordWrap: 'break-word',
									whiteSpace: 'normal',
									lineHeight: '1.2'
								}}>Closing Amt<br/><span className="text-danger">*</span></th>
								<th style={{
									width: '14%',
									padding: '4px 6px',
									backgroundColor: '#e9ecef',
									borderBottom: '2px solid #dee2e6',
									wordWrap: 'break-word',
									whiteSpace: 'normal',
									lineHeight: '1.2'
								}}>Credit Card Slips<br/><span className="text-danger">*</span></th>
								<th style={{
									width: '14%',
									padding: '4px 6px',
									backgroundColor: '#e9ecef',
									borderBottom: '2px solid #dee2e6',
									wordWrap: 'break-word',
									whiteSpace: 'normal',
									lineHeight: '1.2'
								}}>Cheques<br/><span className="text-danger">*</span></th>
							</tr>
						</thead>
						<tbody>
							{registers.map((register, idx) => {
								const input = registerInputs.find((i) => i.id === register.id) || {};
								const error = inputErrors[register.id] || {};
								return (
									<tr key={register.id || idx} style={{ verticalAlign: 'middle' }}>
										<td className="text-center" style={{ padding: '4px 2px', width: '30px', fontSize: '11px' }}>{idx + 1}</td>
										<td style={{ padding: '4px 6px', fontSize: '11px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
											<OverlayTrigger
												trigger={['hover', 'focus']}
												placement="top"
												overlay={
													<Tooltip>{register.BillingCounter?.counter_name || 'N/A'}</Tooltip>
												}
											>
												<span>{register.BillingCounter?.counter_name || 'N/A'}</span>
											</OverlayTrigger>
										</td>
										<td style={{ padding: '4px 6px', fontSize: '11px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
											{register.User
												? `${register.User.first_name} ${register.User.last_name}`
												: 'N/A'}
										</td>
										<td style={{ padding: '4px 6px', fontSize: '11px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
											{register.opening_date ? (
												<>
													<div>{new Date(register.opening_date).toLocaleDateString('en-GB')}</div>
													<div style={{ fontSize: '9px', color: '#6c757d' }}>
														{new Date(register.opening_date).toLocaleTimeString('en-US', {
															hour: '2-digit',
															minute: '2-digit',
															hour12: true
														})}
													</div>
												</>
											) : 'N/A'}
										</td>
										<td className="text-end" style={{ padding: '4px 6px', fontSize: '11px' }}>
											₹{Number(register.opening_amount || 0).toLocaleString('en-IN', {
												minimumFractionDigits: 2
											})}
										</td>

										<td className="text-end" style={{ padding: '4px 6px', fontSize: '11px' }}>
											₹{Number(register.total_order_value || 0).toLocaleString('en-IN', {
												minimumFractionDigits: 2
											})}
										</td>
										{['closing_amount', 'credit_card_slips', 'cheques'].map((field) => {
											const hasError = shouldShowError(register.id, field);
											const fieldValue = input[field] !== undefined && input[field] !== null && input[field] !== '' ? input[field] : '';
											return (
												<td key={field} style={{ padding: '4px 6px' }}>
													<Form.Control
														type="number"
														size="sm"
														value={fieldValue}
														onChange={(e) =>
															handleInputChange(
																register.id,
																field,
																e.target.value
															)
														}
														onBlur={() => handleFieldBlur(register.id, field)}
														min={0}
														disabled={loading}
														style={{
															height: '26px',
															fontSize: '11px',
															padding: '1px 4px'
														}}
														isInvalid={hasError}
													/>
													{hasError && (
														<small className="text-danger" style={{ fontSize: '9px', display: 'block' }}>
															{error[field]}
														</small>
													)}
												</td>
											);
										})}
									</tr>
								);
							})}
						</tbody>
					</Table>
				</div>

				{Object.keys(inputErrors).length > 0 && (
					<Alert variant="danger" className="py-1 mb-2" style={{ fontSize: '0.75rem' }}>
						<p className="mb-0 fw-bold">Please fill in all mandatory fields marked with *</p>
					</Alert>
				)}

				<Form.Group className="mb-2">
					<Form.Label className="fw-bold" style={{ fontSize: '0.85rem', marginBottom: '2px' }}>
						Reason for Closure <span className="text-danger">*</span>
					</Form.Label>
					<Form.Control
						as="textarea"
						rows={2}
						value={reason}
						onChange={(e) => {
							setReason(e.target.value);
							setReasonError('');
						}}
						placeholder="Provide reason for closing while registers are open..."
						isInvalid={!!reasonError}
						disabled={loading}
						maxLength={500}
						style={{ fontSize: '0.85rem', padding: '4px 8px', height: '50px' }}
					/>
					<div className="d-flex justify-content-between mt-1">
						<small className={reasonError ? 'text-danger' : 'text-muted'} style={{ fontSize: '0.7rem' }}>
							{reasonError || 'Minimum 10 characters'}
						</small>
						<small className="text-muted" style={{ fontSize: '0.7rem' }}>{reason.length}/500</small>
					</div>
				</Form.Group>

				<Alert variant="info" className="py-1 mb-2" style={{ fontSize: '0.8rem' }}>
					<div className="d-flex align-items-start">
						<FontAwesomeIcon icon={faInfoCircle} className="me-2 mt-1 text-info" style={{ fontSize: '0.8rem' }} />
						<div>
							<p className="fw-bold mb-0" style={{ fontSize: '0.8rem' }}>Confirm Action</p>
							<div className="mb-0 ps-3" style={{ fontSize: '0.75rem' }}>
								<p>All open registers will be closed automatically. This cannot be undone.</p>
							</div>
						</div>
					</div>
				</Alert>

				<Form.Check
					type="checkbox"
					id="confirm-close-registers"
					label="I confirm I want to proceed with closing the business day."
					checked={confirmed}
					onChange={(e) => setConfirmed(e.target.checked)}
					disabled={loading}
					className="mt-1"
					style={{ fontSize: '0.85rem' }}
				/>
			</Modal.Body>

			<Modal.Footer className="d-flex justify-content-between py-2">
				<Button variant="light" onClick={onCancel} disabled={loading} className="px-4" size="sm">
					Cancel
				</Button>
				<Button
					variant="danger"
					onClick={handleConfirm}
					disabled={loading || !confirmed || reason.trim().length < 10}
					className="px-4"
					size="sm"
				>
					{loading ? (
						<>
							<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
							Closing...
						</>
					) : (
						<>
							<FontAwesomeIcon icon={faLock} className="me-2" />
							Proceed
						</>
					)}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default OpenRegistersCloseModal;